import * as yup from 'yup';
import { OrgRole } from '../components/Wizard.type';
import { OrgTypeFlags } from './getOrgTypeFlags';
import { orgTypeNames } from './getOrgTypeFlags';
import { LegalSubject } from './getOrgTypeFlags';
import { getOrgTypeFlags } from './getOrgTypeFlags';
import { isINN10 } from './checkINN';
import { isINN12 } from './checkINN';
import { checkIsFilial } from './checkIsFilial';

const emptyValue = (name: string) => `Заполните поле «${name}»`;
const shortValue = (name: string, minLength: number) =>
    `Поле «${name}» должно содержать не менее ${minLength} символов`;

export const messages = {
    invalid_role: 'Выберите роль организации на площадке',
    emptyLastName: emptyValue('Фамилия'),
    invalidLastName: shortValue('Фамилия', 2),
    emptyFirstName: emptyValue('Имя'),
    invalidFirstName: shortValue('Имя', 2),
    emptySecondName: emptyValue('Отчество'),
    invalidSecondName: shortValue('Отчество', 2),
    emptyWorkPhone: emptyValue('Номер телефона'),
    invalidWorkPhone: 'Неверный формат номера телефона',
    emptyEmail: emptyValue('Email'),
    invalidEmail: 'Неверный формат адреса email',
    weakPassword:
        'Пароль должен быть не менее 8 символов длиной и содержать цифры, заглавные и прописные латинские буквы',
    emptyOrgType: 'Выберите значение субъекта права',
    emptyOrgForm: 'Выберите значение организационно-правовой формы',
    invalidOrgForm:
        'Значение организационно-правовой формы не соответствует значению субъекта права',
    emptyOrgName: emptyValue('Наименование организации'),
    invalidOrgName: shortValue('Наименование организации', 3),
    emptyRegion: 'Выберите регион',
    invalidFullName:
        'Для выбранного субъекта права наименование организации не должно указываться',
    emptyKpp: emptyValue('КПП'),
    emptyInn: emptyValue('ИНН'),
    shortInn10: 'Поле ИНН должно содержать 10 цифр',
    shortInn12: 'Поле ИНН должно содержать 12 цифр',
    invalidInn: 'Указано некорректное значение ИНН',
    invalidKpp: 'Поле КПП должно содержать 9 цифр',
    personal: 'Необходимо согласие на обработку персональных данных',
    notAllowedEmail:
        'Пользователь с таким email уже существует и не может быть зарегистрирован, для входа в систему воспользуйтесь уже созданным логином и паролем. Если вы забыли пароль, восстановите его через систему восстановления пароля. Или используйте другой электронный адрес для регистрации',
    notAllowedOrgFL:
        'Физическое лицо с таким ИНН уже зарегистрировано. Обратитесь в контактный центр для получения консультации',
    notAllowedOrgIP:
        'Индивидуальный предприниматель с таким ИНН уже зарегистрирован. Обратитесь в контактный центр для получения консультации',
    notAllowedOrgUL:
        'Компания с такими ИНН и КПП уже зарегистрирована. Обратитесь в контактный центр для получения консультации',
    notAllowedDuplicateInn:
        'Компания с таким ИНН уже зарегистрирована, при этом КПП не соответствует маске филиала или обособленного подразделения. Вам необходимо обратиться в контактный центр для возобновления существующей учётной записи',
};

export const emailValidationSchema = yup
    .string()
    .required(messages.emptyEmail)
    .email(messages.invalidEmail);

export const passwordValidationSchema = yup
    .string()
    .required(messages.weakPassword)
    .min(8, messages.weakPassword)
    .matches(/[a-z]/, messages.weakPassword)
    .matches(/[A-Z]/, messages.weakPassword)
    .matches(/\d/, messages.weakPassword);

interface FirstStepParams {
    checkUserRegistrationAvailable: (email: string) => Promise<boolean>;
}

export const createFirstStepValidationSchema = ({
    checkUserRegistrationAvailable,
}: FirstStepParams) =>
    yup.object({
        user: yup.object({
            lastName: yup
                .string()
                .required(messages.emptyLastName)
                .min(2, messages.invalidLastName),
            firstName: yup
                .string()
                .required(messages.emptyFirstName)
                .min(2, messages.invalidFirstName),
            secondName: yup
                .string()
                .default('')
                .defined()
                .test(
                    'is-valid-second-name',
                    messages.invalidSecondName,
                    (value?: string) => {
                        return !value || value.length >= 2;
                    }
                ),
            workPhone: yup
                .string()
                .required(messages.emptyWorkPhone)
                .test(
                    'min-10-digits',
                    messages.invalidWorkPhone,
                    (value?: string) => {
                        if (value) {
                            const indexOfExt = value.search(/[^+\-\d\s()]/);
                            const valueWithoutExt =
                                indexOfExt >= 0
                                    ? value.slice(
                                          0,
                                          value.search(/[^+\-\d\s()]/)
                                      )
                                    : value;
                            return (
                                valueWithoutExt.replace(/[^\d]/g, '').length >=
                                10
                            );
                        }
                        return true;
                    }
                ),
            email: emailValidationSchema.test(
                'is-user-registration-available',
                messages.notAllowedEmail,
                async (email) => {
                    return email
                        ? await checkUserRegistrationAvailable(email)
                        : true;
                }
            ),
            password: passwordValidationSchema,
        }),
        organization: yup.object({
            role: yup
                .string()
                .nullable()
                .oneOf(['customer', 'participant'], messages.invalid_role)
                .required(messages.invalid_role),
        }),
    });

const checkInn = (
    inn: string | undefined,
    { isFL, isIP, isUL }: OrgTypeFlags
): string => {
    if (!inn) {
        return messages.emptyInn;
    }
    if (isFL || isIP) {
        if (inn.length !== 12) {
            return messages.shortInn12;
        }
        if (!isINN12(inn)) {
            return messages.invalidInn;
        }
    }
    if (isUL) {
        if (inn.length !== 10) {
            return messages.shortInn10;
        }
        if (!isINN10(inn)) {
            return messages.invalidInn;
        }
    }
    return '';
};

const checkKpp = (
    kpp: string | undefined,
    { isUL }: Pick<OrgTypeFlags, 'isUL'>
): string => {
    if (isUL) {
        if (!kpp) {
            return messages.emptyKpp;
        }
        if (!/\d{9}/.test(kpp)) {
            return messages.invalidKpp;
        }
    }
    return '';
};

interface SecondStepParams {
    checkOrgRegistrationAvailable: (
        inn: string,
        kpp?: string
    ) => Promise<boolean>;
}

export const createSecondStepValidationSchema = ({
    checkOrgRegistrationAvailable,
}: SecondStepParams) =>
    yup.object({
        organization: yup.object({
            role: yup
                .string()
                .nullable()
                .required(messages.invalid_role)
                .oneOf<OrgRole>(
                    ['customer', 'participant'],
                    messages.invalid_role
                ),
            orgType: yup
                .object({
                    id: yup.number().required(messages.emptyOrgType),
                    title: yup.string().required(messages.emptyOrgType),
                })
                .nullable()
                .required(messages.emptyOrgType),
            orgForm: yup
                .object({
                    id: yup.number().required(messages.emptyOrgForm),
                    name: yup.string().required(messages.emptyOrgForm),
                })
                .nullable()
                .test(
                    'is-valid-org-form-name',
                    'Значение ${path}=${value} не найдено в справочнике',
                    async (orgForm, { parent, createError }) => {
                        if (parent.orgType) {
                            const { isFL, isIP, isUL } = getOrgTypeFlags(
                                parent.orgType.title as LegalSubject
                            );

                            if (isFL) {
                                if (orgForm !== null) {
                                    return createError({
                                        message: messages.invalidOrgForm,
                                    });
                                }
                                return true;
                            }

                            if (!orgForm) {
                                return createError({
                                    message: messages.emptyOrgForm,
                                });
                            }

                            if (isIP) {
                                if (orgForm.name !== orgTypeNames.IP) {
                                    return createError({
                                        message: messages.invalidOrgForm,
                                    });
                                }
                            }

                            if (isUL) {
                                if (orgForm.name === orgTypeNames.IP) {
                                    return createError({
                                        message: messages.invalidOrgForm,
                                    });
                                }
                            }
                        }

                        return true;
                    }
                ),
            inn: yup
                .string()
                .required(messages.emptyInn)
                .test(
                    'is-valid-inn',
                    messages.invalidInn,
                    async (inn, { parent, createError }) => {
                        const { isFL, isIP, isUL } = getOrgTypeFlags(
                            parent.orgType.title as LegalSubject
                        );
                        const message = checkInn(inn, { isFL, isIP, isUL });
                        if (message) {
                            return createError({ message });
                        }
                        return true;
                    }
                )
                .test(
                    'is-org-registration-available',
                    async (inn, { parent, createError }) => {
                        const { orgType, kpp } = parent;
                        if (orgType && inn) {
                            const { isFL, isIP, isUL } = getOrgTypeFlags(
                                orgType.title as LegalSubject
                            );
                            const invalidInnMessage = checkInn(inn, {
                                isFL,
                                isIP,
                                isUL,
                            });
                            const invalidKppMessage = checkKpp(kpp, { isUL });
                            if (!invalidInnMessage && !invalidKppMessage) {
                                if (
                                    !(await checkOrgRegistrationAvailable(
                                        inn,
                                        kpp
                                    ))
                                ) {
                                    const message =
                                        (isFL && messages.notAllowedOrgFL) ||
                                        (isIP && messages.notAllowedOrgIP) ||
                                        messages.notAllowedOrgUL;
                                    return createError({ message });
                                }
                                const isFilial = checkIsFilial(kpp);
                                if (
                                    !(await checkOrgRegistrationAvailable(
                                        inn
                                    )) &&
                                    !isFilial &&
                                    !isFL &&
                                    !isIP
                                ) {
                                    return createError({
                                        message:
                                            messages.notAllowedDuplicateInn,
                                    });
                                }
                            }
                        }
                        return true;
                    }
                ),
            kpp: yup
                .string()
                .default('')
                .defined()
                .test('is-valid-kpp', async (kpp, { parent, createError }) => {
                    const { isUL } = getOrgTypeFlags(
                        parent.orgType.title as LegalSubject
                    );
                    const message = checkKpp(kpp, { isUL });
                    if (message) {
                        return createError({ message });
                    }
                    return true;
                }),
            fullName: yup
                .string()
                .default('')
                .defined()
                .test(
                    'is-valid-org-full-name',
                    async (fullName, { parent, createError }) => {
                        if (parent.orgType) {
                            const { isFL, isIP, isUL } = getOrgTypeFlags(
                                parent.orgType.title as LegalSubject
                            );

                            if (isIP || isUL) {
                                if (!fullName) {
                                    return createError({
                                        message: messages.emptyOrgName,
                                    });
                                }
                                if (fullName.length < 2) {
                                    return createError({
                                        message: messages.invalidOrgName,
                                    });
                                }
                            }

                            if (isFL && fullName !== '') {
                                return createError({
                                    message: messages.invalidFullName,
                                });
                            }
                        }
                        return true;
                    }
                ),
            isFilial: yup.boolean().required(),
            region: yup
                .object({
                    regionCode: yup.string().required(messages.emptyRegion),
                })
                .nullable()
                .required(messages.emptyRegion),
        }),
        personal: yup
            .boolean()
            .required(messages.personal)
            .oneOf([true], messages.personal),
    });

export type ValidatedUser = yup.InferType<
    ReturnType<typeof createFirstStepValidationSchema>
>;
export type ValidatedOrganization = yup.InferType<
    ReturnType<typeof createSecondStepValidationSchema>
>;
